/**
 * Persisted theme store: dark mode, branding colors, logos, font, and custom CSS.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { BrandingLogos, BrandingColors, CompleteBrandingResponse } from '@/types/branding.types';

/** Persisted branding + dark-mode state applied as CSS custom properties. */
interface ThemeStore {
  isDarkMode: boolean;
  logos: BrandingLogos;
  colors: BrandingColors;
  customCss: string | null;
  fontFamily: string | null;
  fontUrl: string | null;
  companyName: string | null;
  companyCountry: string | null;
  /** Persist dark mode and rewrite CSS custom properties. */
  setDarkMode: (_isDark: boolean) => void;
  /** Replace branding (colors, logos, font, CSS) then {@link ThemeStore.applyTheme}. */
  setBranding: (_branding: CompleteBrandingResponse) => void;
  /** Write branding tokens onto `document.documentElement` without a transition flash. */
  applyTheme: () => void;
}

/** Fallback palette when branding fetch fails or a nuance is missing. */
const defaultColors: BrandingColors = {
  color2: {
    nuance1: { light: '#2C5282', dark: '#3D6BA8' },
    nuance2: { light: '#1E3A5F', dark: '#2C5282' },
    nuance3: { light: '#1A2F4A', dark: '#1E3A5F' },
  },
  color1: {
    nuance1: { light: '#FF8C42', dark: '#FF7020' },
    nuance2: { light: '#FF6B2E', dark: '#E65A1A' },
    nuance3: { light: '#E65A1A', dark: '#CC3F00' },
  },
  color3: {
    nuance1: { light: '#2DD4BF', dark: '#5EEAD4' },
    nuance2: { light: '#14B8A6', dark: '#2DD4BF' },
    nuance3: { light: '#0F766E', dark: '#14B8A6' },
  },
};

/** Drop `theme-no-transition` after two frames so the switch does not animate. */
const endThemeSwitch = (root: HTMLElement) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => root.classList.remove('theme-no-transition'));
  });
};

/** Theme store: dark mode, logos, colors, font, and {@link ThemeStore.applyTheme} on the document. */
export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      isDarkMode: false,
      logos: {
        logo1: { light: '', dark: '' },
        logo2: { light: '', dark: '' },
        logo3: { light: '', dark: '' },
        favicon: null,
      },
      colors: defaultColors,
      customCss: null,
      fontFamily: null,
      fontUrl: null,
      companyName: null,
      companyCountry: null,

      setDarkMode: (isDark: boolean) => {
        set({ isDarkMode: isDark });
        get().applyTheme();
      },

      setBranding: (branding: CompleteBrandingResponse) => {
        set({
          logos: branding.logos,
          colors: {
            color2: {
              nuance1: {
                light: branding.colors.color2.nuance1.light || defaultColors.color2.nuance1.light,
                dark: branding.colors.color2.nuance1.dark || defaultColors.color2.nuance1.dark,
              },
              nuance2: {
                light: branding.colors.color2.nuance2.light || defaultColors.color2.nuance2.light,
                dark: branding.colors.color2.nuance2.dark || defaultColors.color2.nuance2.dark,
              },
              nuance3: {
                light: branding.colors.color2.nuance3.light || defaultColors.color2.nuance3.light,
                dark: branding.colors.color2.nuance3.dark || defaultColors.color2.nuance3.dark,
              },
            },
            color1: {
              nuance1: {
                light: branding.colors.color1.nuance1.light || defaultColors.color1.nuance1.light,
                dark: branding.colors.color1.nuance1.dark || defaultColors.color1.nuance1.dark,
              },
              nuance2: {
                light: branding.colors.color1.nuance2.light || defaultColors.color1.nuance2.light,
                dark: branding.colors.color1.nuance2.dark || defaultColors.color1.nuance2.dark,
              },
              nuance3: {
                light: branding.colors.color1.nuance3.light || defaultColors.color1.nuance3.light,
                dark: branding.colors.color1.nuance3.dark || defaultColors.color1.nuance3.dark,
              },
            },
            color3: {
              nuance1: {
                light: branding.colors.color3.nuance1.light || defaultColors.color3.nuance1.light,
                dark: branding.colors.color3.nuance1.dark || defaultColors.color3.nuance1.dark,
              },
              nuance2: {
                light: branding.colors.color3.nuance2.light || defaultColors.color3.nuance2.light,
                dark: branding.colors.color3.nuance2.dark || defaultColors.color3.nuance2.dark,
              },
              nuance3: {
                light: branding.colors.color3.nuance3.light || defaultColors.color3.nuance3.light,
                dark: branding.colors.color3.nuance3.dark || defaultColors.color3.nuance3.dark,
              },
            },
          },
          customCss: branding.customCss,
          fontFamily: branding.fontFamily,
          fontUrl: branding.fontUrl,
          companyName: branding.companyName ?? null,
          companyCountry: branding.companyCountry ?? null,
        });
        get().applyTheme();
      },

      applyTheme: () => {
        const root = document.documentElement;
        root.classList.add('theme-no-transition');
        root.getBoundingClientRect();

        try {
          const { isDarkMode, colors, customCss, fontFamily, fontUrl, companyName } = get();
          const mode = isDarkMode ? 'dark' : 'light';

          if (companyName) {
            document.title = companyName;
          }

          // color2 = Primary (buttons, icons, menus)
          root.style.setProperty('--color-2-nuance-1', colors.color2.nuance1[mode]);
          root.style.setProperty('--color-2-nuance-2', colors.color2.nuance2[mode]);
          root.style.setProperty('--color-2-nuance-3', colors.color2.nuance3[mode]);

          // color1 = Secondary (titles, colored borders)
          root.style.setProperty('--color-1-nuance-1', colors.color1.nuance1[mode]);
          root.style.setProperty('--color-1-nuance-2', colors.color1.nuance2[mode]);
          root.style.setProperty('--color-1-nuance-3', colors.color1.nuance3[mode]);

          // color3 = Accent (spinners, loaders)
          root.style.setProperty('--color-3-nuance-1', colors.color3.nuance1[mode]);
          root.style.setProperty('--color-3-nuance-2', colors.color3.nuance2[mode]);
          root.style.setProperty('--color-3-nuance-3', colors.color3.nuance3[mode]);

          root.classList.toggle('dark', isDarkMode);

          const { logos: brandingLogos } = get();
          if (brandingLogos.favicon) {
            let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
            if (!favicon) {
              favicon = document.createElement('link');
              favicon.rel = 'icon';
              document.head.appendChild(favicon);
            }
            favicon.href = brandingLogos.favicon;
          }

          if (fontFamily) {
            root.style.setProperty('--font-family', fontFamily);
          }

          if (fontUrl) {
            const linkId = 'custom-font-link';
            let link = document.getElementById(linkId) as HTMLLinkElement;
            if (!link) {
              link = document.createElement('link');
              link.id = linkId;
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
            link.href = fontUrl;
          }

          if (customCss) {
            const styleId = 'custom-branding-css';
            let style = document.getElementById(styleId);
            if (!style) {
              style = document.createElement('style');
              style.id = styleId;
              document.head.appendChild(style);
            }
            style.textContent = customCss;
          }
        } finally {
          endThemeSwitch(root);
        }
      },
    }),
    {
      name: 'ednov-theme',
      partialize: (state) => ({
        isDarkMode: state.isDarkMode,
        logos: state.logos,
        colors: state.colors,
        companyCountry: state.companyCountry,
        companyName: state.companyName,
      }),
    },
  ),
);

/** Returns the stored company country from branding. */
export function getCompanyCountry(): string | null {
  return useThemeStore.getState().companyCountry;
}
